import React, { useState } from 'react';
import { RootState } from '../../../rootReducer';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import logoFb from '../../../assets/sso/fb.svg';
import logoOs from '../../../assets/sso/os.svg';
import iconMagic from '../../../assets/IconMagic.svg';
import logoGmb from '../../../assets/sso/gmb.svg';
import { useSocialNetworkData } from '../../../reviews/utils/SocialNetworkData';
import { getCountUnreadReviewsByPlatforms } from '../../../reviews/services/SocialNetworkService';
import { redirectToMyPrevisite } from '../../../service/authService';
import ModalOpinionSystemConnect from '../../../reviews/components/ModalOpinionSystemConnect';
import { NetworkCard } from '../../../reviews/components/NetworkCard';
import { useUser } from '../../../hooks/useUser';

const Info: React.FC = () => {
    const navigate = useNavigate();
    const [refreshReviews] = useState(0);
    const { reviews, loadingState, platformRatings } = useSocialNetworkData(
        useSelector((state: RootState) => state.user.userMyId) || '',
        refreshReviews,
    );

    const userMyId = useSelector((state: RootState) => state.user.userMyId);
    const [showConnectionModal, setShowConnectionModal] = useState<boolean>(false);
    const { user, isLoading: isUserLoading } = useUser();

    const handleRedirect = () => {
        redirectToMyPrevisite(userMyId);
    };

    const openModal = () => {
        setShowConnectionModal(true);
    };

    const handleOpinionSystemConnected = async () => {
        try {
            setShowConnectionModal(false);

            await new Promise(resolve => setTimeout(resolve, 1000));

            window.location.reload();
        } catch (error) {
            console.error('❌ Error during refresh after connection :', error);
            window.location.reload();
        }
    };

    const handleCardClick = (platform: string, unreadCount: number) => {
        if (unreadCount > 0) {
            const searchParams = new URLSearchParams({
                status: 'unread',
                network: platform,
            });
            navigate(`/reviews?${searchParams.toString()}`);
        }
    };

    const gmbRating = platformRatings.find(r => r.platform === 'google_my_business');
    const fbRating = platformRatings.find(r => r.platform === 'facebook');
    const osRating = platformRatings.find(r => r.platform === 'opinion_system');
    const countUnreadReviewsByPlatforms = getCountUnreadReviewsByPlatforms(reviews);

    return (
        <div className="h-full w-full px-5 py-6 rounded-2xl inline-flex flex-col justify-center items-start gap-5 overflow-hidden">
            <div className="self-stretch inline-flex justify-start items-center gap-2 mt-1">
                <div className="p-1.5 bg-primary rounded-lg flex justify-start items-center gap-2.5">
                    <div className="w-6 h-6 relative">
                        <img
                            className="absolute left-0.5 top-0.5"
                            src={iconMagic}
                            alt="icon magic"
                        />
                    </div>
                </div>
                <div className="flex-1 justify-center text-neutral-900 text-medium font-semibold leading-normal">
                    Gestion des avis
                </div>
            </div>
            <div className="h-full w-full flex flex-col xl:flex-row gap-5 justify-center justify-items-center items-start relative">
                {showConnectionModal && (
                    <ModalOpinionSystemConnect
                        onClose={() => setShowConnectionModal(false)}
                        isLoading={loadingState.opinionSystem}
                        setShowConnectionModal={setShowConnectionModal}
                        userId={userMyId}
                        onSuccess={handleOpinionSystemConnected}
                    />
                )}

                <NetworkCard
                    network="google_my_business"
                    isConnected={!!user?.gmb}
                    isLoading={isUserLoading || loadingState.gmb}
                    rating={gmbRating}
                    unreadCount={countUnreadReviewsByPlatforms?.unreadCountGmb || 0}
                    logo={logoGmb}
                    name="Google"
                    onConnect={handleRedirect}
                    onCardClick={handleCardClick}
                />

                <NetworkCard
                    network="facebook"
                    isConnected={!!user?.fb}
                    isLoading={isUserLoading || loadingState.facebook}
                    rating={fbRating}
                    unreadCount={countUnreadReviewsByPlatforms?.unreadCountFb || 0}
                    logo={logoFb}
                    name="Facebook"
                    onConnect={handleRedirect}
                    onCardClick={handleCardClick}
                />

                <NetworkCard
                    network="opinion_system"
                    isConnected={!!user?.opinionSystem}
                    isLoading={isUserLoading || loadingState.opinionSystem}
                    rating={osRating}
                    unreadCount={countUnreadReviewsByPlatforms?.unreadCountOS || 0}
                    logo={logoOs}
                    name="Opinion System"
                    onConnect={openModal}
                    onCardClick={handleCardClick}
                />
            </div>
        </div>
    );
};

export default Info;
